import { fetchAuthSession } from 'aws-amplify/auth';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { getSecretValue } from 'src/services/aws/SecretManagerService';
import { BackendGraphQLApiConfig, SecretKeyArnConfig, SecretManagerKeys } from '../../constants/ApiConfigConstants';
import { EventsApiConfig } from './AppContextModels';
import { AppSyncService } from 'src/services/aws/AppSyncService';

/**
 * Returns the Amplify authentication configuration.
 */
export const getAmplifyAuthConfig = () => {
  const { Region, CognitoUserPoolId, CognitoUserPoolWebClientId, CognitoIdentityPoolId, AwsAccountId, CognitoDomain, ApplicationDomain } =
    ENVIRONMENT_VARIABLES.env;

  return {
    Auth: {
      Cognito: {
        userPoolId: CognitoUserPoolId,
        userPoolClientId: CognitoUserPoolWebClientId,
        identityPoolId: CognitoIdentityPoolId,
        region: Region,
        loginWith: {
          oauth: {
            domain: CognitoDomain,
            scopes: ['openid'],
            redirectSignIn: [ApplicationDomain],
            redirectSignOut: [ApplicationDomain],
            responseType: 'code' as const
          }
        }
      }
    }
  };
};

/**
 * Retrieves both API keys from a single Secrets Manager call.
 * For dev and alpha environments only; other environments use Cognito auth.
 */
const getApiKeys = async (stage: string, secretArn: string | undefined, region: string): Promise<{ apiKey: string; eventsApiKey: string }> => {
  if (stage !== 'dev' && stage !== 'alpha') return { apiKey: '', eventsApiKey: '' };

  const apiSecrets = secretArn && (await getSecretValue(secretArn, region))?.SecretString;
  if (!apiSecrets) return { apiKey: '', eventsApiKey: '' };

  const parsedSecrets = JSON.parse(apiSecrets);
  return {
    apiKey: parsedSecrets[SecretManagerKeys.API_KEY] || '',
    eventsApiKey: parsedSecrets[SecretManagerKeys.EVENTS_API_KEY] || ''
  };
};

/**
 * Retrieves AppSync configuration from AWS Secrets Manager.
 */
export const getAppSyncConfig = async (): Promise<any> => {
  console.debug(`Current Stage: ${ENVIRONMENT_VARIABLES.env.Stage}`);
  const { AuthenticationType, Stage, Region } = ENVIRONMENT_VARIABLES.env;
  const secretArn = SecretKeyArnConfig.find((config) => config.environment === Stage)?.secretARN;

  try {
    const { apiKey: graphQlAPIKey, eventsApiKey: eventsAPIKey } = await getApiKeys(Stage, secretArn, Region);
    const graphQlApiEndpoint = await AppSyncService.getAppSyncGraphQLEndpoint();
    const backendEventsApiEndpoint = await AppSyncService.getAppSyncEventsEndpoint();

    const eventsConfig: EventsApiConfig = {
      endpoint: backendEventsApiEndpoint!,
      region: Region,
      defaultAuthMode: AuthenticationType
    };

    if (AuthenticationType === 'apiKey') {
      eventsConfig.apiKey = eventsAPIKey;
    }

    return {
      API: {
        GraphQL: {
          endpoint: graphQlApiEndpoint,
          region: Region,
          defaultAuthMode: AuthenticationType,
          apiKey: graphQlAPIKey
        },
        Events: eventsConfig
      }
    };
  } catch (error: any) {
    console.error('Unable to retrieve API Key', error);
    throw error;
  }
};

/**
 * Retrieves Rest API configuration.
 * us-west-2 is the region for FINTECH_PAPI and BASEBALL_CARD_API.
 */
export const getRestApiConfig = () => {
  const { Stage } = ENVIRONMENT_VARIABLES.env;
  const getApiEndpoint = (environment: string) => BackendGraphQLApiConfig.find((config) => config.environment === environment)?.risk_remediator_api;
  const getFintechPapiEndpoint = (environment: string) => BackendGraphQLApiConfig.find((config) => config.environment === environment)?.fintech_papi;
  const getBaseballCardApiEndpoint = (environment: string) =>
    BackendGraphQLApiConfig.find((config) => config.environment === environment)?.baseball_card_api;
  const getBrassApiEndpoint = (environment: string) => BackendGraphQLApiConfig.find((config) => config.environment === environment)?.brass_api;
  const getFinsuiteRestApiEndpoint = (environment: string) =>
    BackendGraphQLApiConfig.find((config) => config.environment === environment)?.finsuite_rest_api;

  const apiEndpoint = getApiEndpoint(Stage);
  const fintechPapiEndpoint = getFintechPapiEndpoint(Stage);
  const baseballCardApiEndpoint = getBaseballCardApiEndpoint(Stage);
  const brassApiEndpoint = getBrassApiEndpoint(Stage);
  const finsuiteRestApiEndpoint = getFinsuiteRestApiEndpoint(Stage);

  return {
    API: {
      REST: {
        RISK_REMEDIATOR_API: {
          endpoint: apiEndpoint,
          region: 'us-east-1'
        },
        FINTECH_PAPI: {
          endpoint: fintechPapiEndpoint,
          region: 'us-west-2'
        },
        BASEBALL_CARD_API: {
          endpoint: baseballCardApiEndpoint,
          region: 'us-west-2'
        },
        BRASS_API: {
          endpoint: brassApiEndpoint,
          region: 'us-west-2'
        },
        FINSUITE_REST_API: {
          endpoint: finsuiteRestApiEndpoint,
          region: 'us-west-2'
        }
      }
    }
  };
};
